import http from 'utils/axios';
import { processResponse } from 'services/dynamicore/functions/response';

const unsafeKeys = ['__proto__', 'prototype', 'constructor'];

export function pathParts(path) {
  const parts = String(path || '').split('.');
  if (parts.some((part) => !/^[\w-]+$/.test(part) || unsafeKeys.includes(part))) {
    throw new Error('Ruta de datos no válida');
  }
  return parts;
}

export function readPath(object, path) {
  if (!path) return object;
  return pathParts(path).reduce((value, key) => (
    value != null && Object.prototype.hasOwnProperty.call(value, key)
      ? value[key]
      : undefined
  ), object);
}

export function writePath(object, path, value) {
  const parts = pathParts(path);
  let target = object;
  parts.forEach((key, index) => {
    if (index === parts.length - 1) {
      target[key] = value;
    } else {
      if (!target[key] || typeof target[key] !== 'object') target[key] = {};
      target = target[key];
    }
  });
  return object;
}

class MissingBinding extends Error {}

export function resolveBindings(value, scope) {
  if (Array.isArray(value)) return value.map((entry) => resolveBindings(entry, scope));
  if (value && typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'from')) {
      if (!/^(context|form)\./.test(value.from)) throw new Error('Origen de parámetro no válido');
      let resolved = readPath(scope, value.from);
      if (resolved === undefined || resolved === null || resolved === '') {
        if (Object.prototype.hasOwnProperty.call(value, 'default')) resolved = value.default;
        else if (value.required === false) return undefined;
        else throw new MissingBinding(value.from);
      }
      return resolved;
    }
    const result = {};
    Object.entries(value).forEach(([key, entry]) => {
      pathParts(key);
      const resolved = resolveBindings(entry, scope);
      if (resolved !== undefined) result[key] = resolved;
    });
    return result;
  }
  return value;
}

export function prepareRemoteRequest(source, scope) {
  try {
    if (!source || !/^\/(?!\/)[^\\?#\s]+$/.test(source.endpoint || '')) {
      throw new Error('El selector requiere un endpoint relativo de la API');
    }
    const method = String(source.method || 'GET').toUpperCase();
    if (!['GET', 'POST'].includes(method)) throw new Error('Método de consulta no soportado');
    const params = resolveBindings(source.params || {}, scope);
    const body = resolveBindings(source.body || {}, scope);
    if (!params || Array.isArray(params) || typeof params !== 'object') {
      throw new Error('Los parámetros deben ser un objeto');
    }
    return {
      request: {
        endpoint: source.endpoint,
        method,
        params,
        body,
      },
    };
  } catch (error) {
    return error instanceof MissingBinding
      ? {
        waiting: true,
        message: `Completa primero ${error.message}`,
      }
      : {
        error: error.message,
      };
  }
}

function responseRows(response, source, params) {
  const envelope = response.data || {};
  if (envelope.status === 'error' || Number(envelope.code) === 0 || Number(envelope.code) >= 400) {
    throw new Error(envelope.message || 'No se pudieron consultar las opciones');
  }
  const result = source.response?.itemsPath
    ? {
      values: readPath(envelope, source.response.itemsPath),
      total: readPath(envelope, source.response.totalPath || 'total'),
    }
    : processResponse(response, params, false, true);
  if (!Array.isArray(result.values)) throw new Error('La consulta no devolvió una lista de opciones');
  return result;
}

export async function fetchRemoteRows(source, request, cancelToken) {
  const { pagination } = source;
  const pageSize = Number(pagination?.pageSize || 25);
  const maxPages = Number(pagination?.maxPages || 100);
  if (pagination && (!Number.isInteger(pageSize) || pageSize < 1 || !Number.isInteger(maxPages) || maxPages < 1)) {
    throw new Error('Paginación no válida');
  }
  const rows = [];
  const pages = new Set();
  for (let page = 1; page <= (pagination ? maxPages : 1); page += 1) {
    const params = {
      ...request.params,
    };
    if (pagination) {
      params[pagination.pageParam || 'page'] = page;
      params[pagination.limitParam || 'limit'] = pageSize;
    }
    const config = {
      params,
      cancelToken,
    };
    const response = request.method === 'POST'
      ? await http.post(request.endpoint, request.body, config)
      : await http.get(request.endpoint, config);
    const result = responseRows(response, source, params);
    const fingerprint = JSON.stringify(result.values);
    if (result.values.length && pages.has(fingerprint)) {
      throw new Error('La API repitió una página de opciones');
    }
    pages.add(fingerprint);
    rows.push(...result.values);
    const total = Number(result.total);
    if (!pagination || !result.values.length || (total > 0 ? rows.length >= total : result.values.length < pageSize)) {
      return rows;
    }
  }
  throw new Error('La lista supera el límite de páginas del selector');
}

export function makeRemoteOptions(rows, { valuePath = 'id', labelTemplate = '{{name}}', source }) {
  const seen = new Set();
  return rows.filter((row) => row && typeof row === 'object').filter((row) => Object.entries(source?.filters || {}).every(
    ([path, expected]) => String(readPath(row, path)) === String(expected),
  )).reduce((options, row) => {
    const id = readPath(row, valuePath) ?? (valuePath === 'id' ? row.value : undefined);
    if (!['string', 'number', 'boolean'].includes(typeof id) || id === '' || seen.has(String(id))) return options;
    seen.add(String(id));
    const label = labelTemplate.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_, path) => String(readPath(row, path) ?? ''));
    options.push({
      id,
      name: label.trim() || String(id),
      record: row,
    });
    return options;
  }, []);
}

export function selectionValues(field, option) {
  const result = {
    [field.name || field.fieldname]: option?.id ?? '',
  };
  Object.entries(field.selectionMap || field.settings?.selectionMap || {}).forEach(([target, from]) => {
    pathParts(target);
    result[target] = option ? readPath(option.record, from) ?? '' : '';
  });
  return result;
}
